import { Component, computed, inject, signal, output, input, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { DIALOG_DATA, DialogRef } from '@angular/cdk/dialog';
import { Subject, debounceTime, distinctUntilChanged, switchMap, catchError, of, takeUntil, Observable } from 'rxjs';
import { PostTypeDropdownComponent } from '../post-type-dropdown/post-type-dropdown.component';
import { DateRangeFilterComponent } from '../date-range-filter/date-range-filter.component';
import { DateRangeValue } from '../date-range-filter/date-range-filter.model';
import { AuthorFilterComponent } from '../author-filter/author-filter.component';
import { AuthorChangeEvent } from '../author-filter/author-filter.model';
import {
  BlogPostSelectorConfig,
  BlogPostSelectorItem,
  BlogPostSelectorResult,
  BlogPostSearchCriteria,
  SelectedBlogPost
} from '../../../models/blog-post-selector.model';
import { PublishStatus } from '../../../models/enums';
import { BlogPostSearchCacheService } from '../../services/blog-post-search-cache.service';
import {
  DEFAULT_INITIAL_LOAD_THRESHOLD,
  DEFAULT_MAX_RESULTS_PER_PAGE,
  DEFAULT_FILTER_STATE,
  SELECTOR_LABELS,
  ARIA_LABELS,
  DATE_PRESETS,
  toISODateString,
  isValidDateRange
} from '../../../config/blog-post-selector.constants';

/**
 * Reusable component for selecting multiple blog posts with server-side search
 * 
 * Features:
 * - Server-side search with debouncing
 * - Client-side caching for performance
 * - Multi-select with checkboxes
 * - Filter by publish status
 * - Light/Dark mode support
 * - Keyboard navigation
 * - Accessibility compliant
 * - Loading states and error handling
 * 
 * @example
 * ```typescript
 * const searchFn = (criteria) => this.blogPostService.searchForSelector(criteria);
 * 
 * const dialogRef = this.dialog.open(BlogPostSelectorComponent, {
 *   data: {
 *     searchFunction: searchFn,
 *     organizationId: 'org-123',
 *     preSelectedIds: ['id1', 'id2']
 *   }
 * });
 * 
 * dialogRef.closed.subscribe(result => {
 *   if (result && !result.cancelled) {
 *     console.log(result.selectedPosts);
 *   }
 * });
 * ```
 */
@Component({
  selector: 'cms-blog-post-selector',
  standalone: true,
  imports: [CommonModule, FormsModule, PostTypeDropdownComponent, DateRangeFilterComponent, AuthorFilterComponent],
  templateUrl: './blog-post-selector.component.html',
  styleUrls: ['./blog-post-selector.component.css']
})
export class BlogPostSelectorComponent implements OnInit, OnDestroy {
  // Inject services
  private dialogData = inject<BlogPostSelectorConfig>(DIALOG_DATA, { optional: true });
  private dialogRef = inject<DialogRef<BlogPostSelectorResult>>(DialogRef, { optional: true });
  private cacheService = inject(BlogPostSearchCacheService);

  // Subjects for cleanup
  private destroy$ = new Subject<void>();
  private searchSubject$ = new Subject<string>();

  // Input properties (for standalone usage without dialog)
  searchFunction = input<((criteria: BlogPostSearchCriteria) => Observable<BlogPostSelectorItem[]>) | undefined>(undefined);
  organizationId = input<string>('');
  preSelectedIds = input<string[]>([]);
  title = input<string>('Select Blog Posts');
  primaryButtonText = input<string>('Select');
  secondaryButtonText = input<string>('Cancel');
  enableSearch = input<boolean>(true);
  enableStatusFilter = input<boolean>(true);
  maxSelection = input<number | undefined>(undefined);
  minSearchLength = input<number>(3);
  searchDebounceMs = input<number>(300);
  
  // Phase 2: Initial load configuration
  enableInitialLoad = input<boolean>(DEFAULT_FILTER_STATE.ENABLE_INITIAL_LOAD);
  initialLoadThreshold = input<number>(DEFAULT_INITIAL_LOAD_THRESHOLD);
  showThresholdMessage = input<boolean>(DEFAULT_FILTER_STATE.SHOW_THRESHOLD_MESSAGE);

  // Output events (for standalone usage)
  selectionConfirmed = output<SelectedBlogPost[]>();
  selectionCancelled = output<void>();

  // Internal state
  protected searchTerm = signal<string>('');
  protected selectedStatusFilter = signal<PublishStatus | 'all'>('all');
  protected selectedPostIds = signal<Set<string>>(new Set());
  protected hoveredPostId = signal<string | null>(null);
  protected searchResults = signal<BlogPostSelectorItem[]>([]);
  protected isLoading = signal<boolean>(false);
  protected hasSearched = signal<boolean>(false);
  protected errorMessage = signal<string | null>(null);
  
  // Phase 2: Initial load state
  protected hasPerformedInitialLoad = signal<boolean>(false);
  protected initialLoadComplete = signal<boolean>(false);
  protected totalAvailablePosts = signal<number>(0);
  protected thresholdExceeded = signal<boolean>(false);

  // Phase 3: Advanced filters accordion state
  protected showAdvancedFilters = signal<boolean>(DEFAULT_FILTER_STATE.SHOW_ADVANCED_FILTERS);

  // Phase 4: Date filter state
  protected dateFrom = signal<string | null>(null);
  protected dateTo = signal<string | null>(null);
  protected updateFrom = signal<string | null>(null);
  protected updateTo = signal<string | null>(null);
  protected selectedDatePreset = signal<string | null>(null);

  // Phase 5: Author filter state (simplified - component handles UI)
  protected selectedAuthorId = signal<string | null>(null);
  protected selectedAuthorName = signal<string>(''); // For display in filter chips

  // Phase 6: Post type filter state
  protected selectedPostTypeId = signal<string | null>(null);
  protected selectedPostTypeName = signal<string>(''); // Phase 7: For display

  // Expose PublishStatus enum to template
  protected readonly PublishStatus = PublishStatus;
  
  // Phase 4: Expose DATE_PRESETS to template
  protected readonly DATE_PRESETS = DATE_PRESETS;
  


  // Computed values
  protected filteredPosts = computed(() => {
    const results = this.searchResults();
    const statusFilter = this.selectedStatusFilter();
    
    // Apply client-side filtering for stable counts
    if (statusFilter === 'all') {
      return results;
    }
    
    return results.filter(post => post.publishStatus === statusFilter);
  });

  protected selectedCount = computed(() => this.selectedPostIds().size);

  protected isMaxSelectionReached = computed(() => {
    const max = this.getMaxSelection();
    return max !== undefined && this.selectedCount() >= max;
  });

  protected canSelectMore = computed(() => !this.isMaxSelectionReached());

  // Always use full search results for stable counts
  protected statusFilterOptions = computed(() => {
    const results = this.searchResults();
    return [
      { value: 'all', label: 'All Status', count: results.length },
      { 
        value: PublishStatus.DRAFT, 
        label: 'Draft', 
        count: results.filter(p => p.publishStatus === PublishStatus.DRAFT).length 
      },
      { 
        value: PublishStatus.INTERNAL, 
        label: 'Internal', 
        count: results.filter(p => p.publishStatus === PublishStatus.INTERNAL).length 
      },
      { 
        value: PublishStatus.PUBLIC, 
        label: 'Published', 
        count: results.filter(p => p.publishStatus === PublishStatus.PUBLIC).length 
      }
    ];
  });

  protected showEmptyState = computed(() => {
    return !this.isLoading() && this.hasSearched() && this.searchResults().length === 0;
  });

  protected showInitialState = computed(() => {
    // Show initial state if:
    // 1. Not loading
    // 2. Haven't searched yet
    // 3. No results
    // 4. Haven't performed initial load OR threshold was exceeded
    return !this.isLoading() && 
           !this.hasSearched() && 
           this.searchResults().length === 0 && 
           (!this.hasPerformedInitialLoad() || this.thresholdExceeded());
  });
  
  // Phase 2: New computed signals for initial load UI
  protected showThresholdExceededMessage = computed(() => {
    return this.thresholdExceeded() && 
           !this.hasSearched() && 
           !this.isLoading() &&
           this.getShowThresholdMessage();
  });
  
  protected showInitialPosts = computed(() => {
    return this.hasPerformedInitialLoad() && 
           !this.thresholdExceeded() && 
           this.searchResults().length > 0 &&
           !this.hasSearched();
  });
  
  protected initialPostsMessage = computed(() => {
    const count = this.searchResults().length;
    return SELECTOR_LABELS.MESSAGES.SHOWING_ALL.replace('{count}', count.toString());
  });
  
  // Expose constants for template access
  protected readonly SELECTOR_LABELS = SELECTOR_LABELS;
  protected readonly ARIA_LABELS = ARIA_LABELS;
  
  // Phase 3: Active filters count for accordion badge
  protected activeFiltersCount = computed(() => {
    let count = 0;
    
    // Count status filter if not 'all'
    if (this.selectedStatusFilter() !== 'all') {
      count++;
    }
    
    // Phase 4: Count date filters
    if (this.dateFrom() || this.dateTo()) {
      count++; // Creation date range counts as one filter
    }
    if (this.updateFrom() || this.updateTo()) {
      count++; // Update date range counts as one filter
    }
    
    // Phase 5: Count author filter
    if (this.selectedAuthorId()) {
      count++;
    }
    
    // Phase 6: Count post type filter
    if (this.selectedPostTypeId()) {
      count++;
    }
    
    return count;
  });

  ngOnInit(): void {
    // Initialize selected posts from dialog data or input
    const preSelected = this.dialogData?.preSelectedIds || this.preSelectedIds();
    if (preSelected.length > 0) {
      this.selectedPostIds.set(new Set(preSelected));
    }

    // Setup debounced search
    this.setupDebouncedSearch();
    
    // Phase 2: Perform initial load if enabled
    if (this.getEnableInitialLoad()) {
      this.performInitialLoad();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Setup debounced search stream
   */
  private setupDebouncedSearch(): void {
    const debounceMs = this.dialogData?.searchDebounceMs ?? this.searchDebounceMs();

    this.searchSubject$.pipe(
      debounceTime(debounceMs),
      distinctUntilChanged(),
      switchMap(term => {
        // Only search if meets minimum length
        const minLength = this.dialogData?.minSearchLength ?? this.minSearchLength();
        if (term.trim().length < minLength) {
          return of([]);
        }

        return this.performSearch(term);
      }),
      takeUntil(this.destroy$)
    ).subscribe({
      next: (results) => {
        this.searchResults.set(results);
        this.hasSearched.set(true);
        this.isLoading.set(false);
        this.errorMessage.set(null);
      },
      error: (error) => {
        console.error('Search error:', error);
        this.errorMessage.set('Failed to search posts. Please try again.');
        this.isLoading.set(false);
      }
    });
  }

  /**
   * Perform search with caching
   */
  private performSearch(searchTerm: string): Observable<BlogPostSelectorItem[]> {
    const criteria: BlogPostSearchCriteria = {
      search: searchTerm,
      // Don't pass publishStatus to server - filter client-side for stable counts
      orgId: this.getOrganizationId(),
      page: 1,
      limit: 50,
      excludeIds: Array.from(this.selectedPostIds())
    };

    // Phase 4: Add date filters to criteria if set
    const dateFrom = this.dateFrom();
    const dateTo = this.dateTo();
    const updateFrom = this.updateFrom();
    const updateTo = this.updateTo();
    const selectedAuthorId = this.selectedAuthorId();
    const selectedPostTypeId = this.selectedPostTypeId();

    if (dateFrom) {
      criteria.dateFrom = dateFrom;
    }
    if (dateTo) {
      criteria.dateTo = dateTo;
    }
    if (updateFrom) {
      criteria.updateFrom = updateFrom;
    }
    if (updateTo) {
      criteria.updateTo = updateTo;
    }
    if (selectedAuthorId) {
      criteria.authorId = selectedAuthorId;
    }
    if (selectedPostTypeId) {
      criteria.postTypeId = selectedPostTypeId;
    }

    // Generate cache key
    const cacheKey = this.cacheService.generateKey(criteria);

    // Check cache first
    const cached = this.cacheService.get<BlogPostSelectorItem[]>(cacheKey);
    if (cached) {
      return of(cached);
    }

    // Fetch from server
    const searchFn = this.getSearchFunction();
    if (!searchFn) {
      console.warn('No search function provided');
      return of([]);
    }

    return searchFn(criteria).pipe(
      catchError(error => {
        console.error('Search failed:', error);
        return of([]);
      }),
      // Cache successful results
      switchMap(results => {
        this.cacheService.set(cacheKey, results);
        return of(results);
      })
    );
  }

  /**
   * Get search function from dialog or input
   */
  private getSearchFunction(): ((criteria: BlogPostSearchCriteria) => Observable<BlogPostSelectorItem[]>) | undefined {
    return this.dialogData?.searchFunction || this.searchFunction();
  }

  /**
   * Get organization ID from dialog or input
   */
  protected getOrganizationId(): string {
    return this.dialogData?.organizationId || this.organizationId();
  }

  /**
   * Get max selection from dialog or input
   */
  private getMaxSelection(): number | undefined {
    return this.dialogData?.maxSelection ?? this.maxSelection();
  }

  /**
   * Get title from dialog or input
   */
  protected getTitle(): string {
    return this.dialogData?.title || this.title();
  }

  /**
   * Get primary button text from dialog or input
   */
  protected getPrimaryButtonText(): string {
    return this.dialogData?.primaryButtonText || this.primaryButtonText();
  }

  /**
   * Get secondary button text from dialog or input
   */
  protected getSecondaryButtonText(): string {
    return this.dialogData?.secondaryButtonText || this.secondaryButtonText();
  }

  /**
   * Check if search is enabled
   */
  protected isSearchEnabled(): boolean {
    return this.dialogData?.enableSearch ?? this.enableSearch();
  }

  /**
   * Check if status filter is enabled
   */
  protected isStatusFilterEnabled(): boolean {
    return this.dialogData?.enableStatusFilter ?? this.enableStatusFilter();
  }

  /**
   * Get minimum search length
   */
  protected getMinSearchLength(): number {
    return this.dialogData?.minSearchLength ?? this.minSearchLength();
  }

  /**
   * Phase 2: Get enable initial load from dialog or input
   */
  private getEnableInitialLoad(): boolean {
    return this.dialogData?.enableInitialLoad ?? this.enableInitialLoad();
  }

  /**
   * Phase 2: Get initial load threshold from dialog or input
   */
  private getInitialLoadThreshold(): number {
    return this.dialogData?.initialLoadThreshold ?? this.initialLoadThreshold();
  }

  /**
   * Phase 2: Get show threshold message from dialog or input (used in template)
   */
  protected getShowThresholdMessage(): boolean {
    return this.dialogData?.showThresholdMessage ?? this.showThresholdMessage();
  }

  /**
   * Phase 2: Perform initial load to check post count and determine display strategy
   * 
   * @remarks
   * This method is called on component initialization if enableInitialLoad is true.
   * It fetches posts up to threshold + 1 to determine if results should be displayed.
   * 
   * **Logic:**
   * - If post count <= threshold: Display all posts immediately
   * - If post count > threshold: Show message prompting user to search/filter
   * - Results are cached for performance
   * 
   * **Performance:**
   * - Only called once on init
   * - Results are cached to avoid duplicate requests
   * - Uses same search infrastructure for consistency
   */
  private performInitialLoad(): void {
    // Check if already performed
    if (this.hasPerformedInitialLoad()) {
      return;
    }

    const threshold = this.getInitialLoadThreshold();
    const orgId = this.getOrganizationId();

    if (!orgId) {
      console.warn('Cannot perform initial load: Organization ID not provided');
      return;
    }

    // Set loading state
    this.isLoading.set(true);
    this.hasPerformedInitialLoad.set(true);

    // Fetch up to threshold posts; if we get exactly threshold results, more may exist
    const criteria: BlogPostSearchCriteria = {
      orgId: orgId,
      page: 1,
      limit: Math.min(threshold, DEFAULT_MAX_RESULTS_PER_PAGE),
      // No search term, no filters - just get posts
    };

    // Generate cache key for initial load
    const cacheKey = this.cacheService.generateKey(criteria);

    // Check cache first
    const cached = this.cacheService.get<BlogPostSelectorItem[]>(cacheKey);
    if (cached) {
      this.handleInitialLoadResults(cached, threshold);
      return;
    }

    // Fetch from server
    const searchFn = this.getSearchFunction();
    if (!searchFn) {
      console.warn('No search function provided for initial load');
      this.isLoading.set(false);
      this.initialLoadComplete.set(true);
      return;
    }

    searchFn(criteria).pipe(
      takeUntil(this.destroy$),
      catchError(error => {
        console.error('Initial load failed:', error);
        this.errorMessage.set('Unable to load posts. Please try searching.');
        this.isLoading.set(false);
        this.initialLoadComplete.set(true);
        this.thresholdExceeded.set(false);
        return of([]);
      })
    ).subscribe({
      next: (results) => {
        // Cache the results
        this.cacheService.set(cacheKey, results);
        this.handleInitialLoadResults(results, threshold);
      }
    });
  }

  /**
   * Phase 2: Handle initial load results and determine display strategy
   * 
   * @param results - The fetched blog posts
   * @param threshold - The configured threshold
   */
  private handleInitialLoadResults(results: BlogPostSelectorItem[], threshold: number): void {
    const actualCount = results.length;
    this.totalAvailablePosts.set(actualCount);

    if (actualCount >= threshold) {
      // At or above threshold - more posts may exist, prompt user to search
      this.thresholdExceeded.set(true);
      this.searchResults.set([]);
    } else {
      // Below threshold - show all results
      this.thresholdExceeded.set(false);
      this.searchResults.set(results);
    }

    this.isLoading.set(false);
    this.initialLoadComplete.set(true);
  }

  /**
   * Toggle post selection
   */
  protected togglePost(postId: string): void {
    const currentSelected = new Set(this.selectedPostIds());
    
    if (currentSelected.has(postId)) {
      currentSelected.delete(postId);
    } else {
      // Check max selection
      if (this.isMaxSelectionReached()) {
        return;
      }
      currentSelected.add(postId);
    }
    
    this.selectedPostIds.set(currentSelected);
  }

  /**
   * Check if a post is selected
   */
  protected isPostSelected(postId: string): boolean {
    return this.selectedPostIds().has(postId);
  }

  /**
   * Check if a post can be selected
   */
  protected canSelectPost(postId: string): boolean {
    return this.isPostSelected(postId) || this.canSelectMore();
  }

  /**
   * Select all filtered posts
   */
  protected selectAll(): void {
    const max = this.getMaxSelection();
    const currentSelected = new Set(this.selectedPostIds());
    const filteredPostIds = this.filteredPosts().map(p => p._id);

    for (const postId of filteredPostIds) {
      if (max !== undefined && currentSelected.size >= max) {
        break;
      }
      currentSelected.add(postId);
    }

    this.selectedPostIds.set(currentSelected);
  }

  /**
   * Deselect all posts
   */
  protected deselectAll(): void {
    this.selectedPostIds.set(new Set());
  }

  /**
   * Clear search
   */
  protected clearSearch(): void {
    this.searchTerm.set('');
    this.searchResults.set([]);
    this.hasSearched.set(false);
    this.errorMessage.set(null);
  }

  /**
   * Phase 3: Toggle advanced filters accordion
   * 
   * @remarks
   * Toggles the visibility of the advanced filters section.
   * Includes smooth animation via CSS transitions.
   * Accessible via keyboard (Enter/Space) and click.
   */
  protected toggleAdvancedFilters(): void {
    this.showAdvancedFilters.update(value => !value);
  }

  /**
   * Phase 3: Clear all active filters
   * 
   * @remarks
   * Resets all filter states to their defaults and re-performs the search.
   * This includes:
   * - Status filter → 'all'
   * - Phase 4: Date filters → null
   * - Future filters (author, post type) will be reset here
   * 
   * Search term and selected posts are NOT cleared.
   */
  protected clearAllFilters(): void {
    // Check if any filters were active before clearing
    const hadActiveFilters = this.activeFiltersCount() > 0;
    
    // Reset status filter
    this.selectedStatusFilter.set('all');
    
    // Phase 4: Reset date filters
    this.dateFrom.set(null);
    this.dateTo.set(null);
    this.updateFrom.set(null);
    this.updateTo.set(null);
    this.selectedDatePreset.set(null);
    
    // Phase 5: Reset author filter
    this.selectedAuthorId.set(null);
    this.selectedAuthorName.set('');
    
    // Phase 6: Reset post type filter
    this.selectedPostTypeId.set(null);
    this.selectedPostTypeName.set('');
    
    // Re-perform search if filters were active or user has searched
    if (hadActiveFilters || this.hasSearched()) {
      this.triggerFilterSearch();
    }
  }

  /**
   * Phase 4: Handle creation date range change from DateRangeFilterComponent
   * 
   * @param range - The date range value from the component
   */
  protected onCreationDateRangeChange(range: DateRangeValue): void {
    if (!range.isValid) return;
    
    this.selectedDatePreset.set(range.preset);
    this.dateFrom.set(range.dateFrom);
    this.dateTo.set(range.dateTo);
    
    // Trigger search immediately
    this.triggerFilterSearch();
  }

  /**
   * Phase 4: Handle update date range change from DateRangeFilterComponent
   * 
   * @param range - The date range value from the component
   */
  protected onUpdateDateRangeChange(range: DateRangeValue): void {
    if (!range.isValid) return;
    
    this.updateFrom.set(range.dateFrom);
    this.updateTo.set(range.dateTo);
    
    // Trigger search immediately
    this.triggerFilterSearch();
  }

  /**
   * Phase 4: Handle date preset selection (DEPRECATED - keeping for backward compatibility)
   * 
   * @param presetValue - The preset identifier (e.g., 'last_7_days')
   * 
   * @remarks
   * Applies predefined date ranges for common use cases.
   * When 'custom' is selected, clears preset and allows manual date entry.
   * Clicking the same preset again will toggle it off.
   * 
   * @deprecated Use DateRangeFilterComponent instead
   */
  protected onDatePresetChange(presetValue: string): void {
    // Toggle off if clicking the same preset
    if (this.selectedDatePreset() === presetValue && presetValue !== 'custom') {
      this.selectedDatePreset.set(null);
      this.dateFrom.set(null);
      this.dateTo.set(null);
      this.triggerFilterSearch();
      return;
    }
    
    this.selectedDatePreset.set(presetValue);
    
    if (presetValue === 'custom') {
      // Clear preset dates, allow manual input
      return;
    }
    
    // Find the preset and apply its date range
    const preset = DATE_PRESETS.find(p => p.value === presetValue);
    if (preset) {
      const { dateFrom, dateTo } = preset.getDateRange();
      this.dateFrom.set(dateFrom);
      this.dateTo.set(dateTo);
      
      // Trigger search immediately
      this.triggerFilterSearch();
    }
  }

  /**
   * Phase 4: Handle creation date 'from' change
   * 
   * @param value - ISO date string from date input
   */
  protected onDateFromChange(value: string): void {
    this.dateFrom.set(value || null);
    this.selectedDatePreset.set('custom');
    
    // Trigger search immediately
    this.triggerFilterSearch();
  }

  /**
   * Phase 4: Handle creation date 'to' change
   * 
   * @param value - ISO date string from date input
   */
  protected onDateToChange(value: string): void {
    this.dateTo.set(value || null);
    this.selectedDatePreset.set('custom');
    
    // Trigger search immediately
    this.triggerFilterSearch();
  }

  /**
   * Phase 4: Handle update date 'from' change
   * 
   * @param value - ISO date string from date input
   */
  protected onUpdateFromChange(value: string): void {
    this.updateFrom.set(value || null);
    
    // Trigger search immediately
    this.triggerFilterSearch();
  }

  /**
   * Phase 4: Handle update date 'to' change
   * 
   * @param value - ISO date string from date input
   */
  protected onUpdateToChange(value: string): void {
    this.updateTo.set(value || null);
    
    // Trigger search immediately
    this.triggerFilterSearch();
  }

  /**
   * Phase 4: Check if creation date range is valid
   * 
   * @returns True if range is valid or if either date is empty
   */
  protected isCreationDateRangeValid(): boolean {
    const from = this.dateFrom();
    const to = this.dateTo();
    return isValidDateRange(from || '', to || '');
  }

  /**
   * Phase 4: Check if update date range is valid
   * 
   * @returns True if range is valid or if either date is empty
   */
  protected isUpdateDateRangeValid(): boolean {
    const from = this.updateFrom();
    const to = this.updateTo();
    return isValidDateRange(from || '', to || '');
  }

  /**
   * Phase 4: Clear creation date filters
   */
  protected clearCreationDateFilter(): void {
    this.dateFrom.set(null);
    this.dateTo.set(null);
    this.selectedDatePreset.set(null);
    
    // Re-perform search immediately
    this.triggerFilterSearch();
  }

  /**
   * Phase 4: Clear update date filters
   */
  protected clearUpdateDateFilter(): void {
    this.updateFrom.set(null);
    this.updateTo.set(null);
    
    // Re-perform search immediately
    this.triggerFilterSearch();
  }



  /**
   * Phase 5: Handle author selection change from AuthorFilterComponent
   * 
   * @param author - Selected author object or null to clear
   */
  protected onAuthorChange(author: AuthorChangeEvent): void {
    this.selectedAuthorId.set(author?.id || null);
    this.selectedAuthorName.set(author?.name || '');
    
    // Trigger search immediately
    this.triggerFilterSearch();
  }



  /**
   * Phase 5: Clear author filter
   */
  protected clearAuthorFilter(): void {
    this.selectedAuthorId.set(null);
    this.selectedAuthorName.set('');
    
    // Trigger search immediately
    this.triggerFilterSearch();
  }
  
  /**
   * Phase 5: Get selected author display name
   * 
   * @returns Author name or empty string
   */
  protected getSelectedAuthorName(): string {
    return this.selectedAuthorName();
  }



  /**
   * Phase 6: Handle post type selection change from dropdown component
   * 
   * @param postTypeId - Selected post type ID or null to clear
   * @param postTypeName - Optional post type name for display
   */
  protected onPostTypeChange(postTypeId: string | null, postTypeName?: string): void {
    this.selectedPostTypeId.set(postTypeId);
    
    // Phase 7: Store the post type name for display in filter chips
    this.selectedPostTypeName.set(postTypeName || '');
    
    // Trigger search immediately
    this.triggerFilterSearch();
  }

  /**
   * Phase 6: Clear post type filter
   */
  protected clearPostTypeFilter(): void {
    this.selectedPostTypeId.set(null);
    this.selectedPostTypeName.set('');
    
    // Trigger search immediately
    this.triggerFilterSearch();
  }

  /**
   * Phase 7: Get status label for display
   * 
   * @param status - PublishStatus value
   * @returns Human-readable status label
   */
  protected getStatusLabel(status: PublishStatus | 'all'): string {
    const statusMap: Record<PublishStatus | 'all', string> = {
      all: 'All',
      [PublishStatus.DRAFT]: 'Draft',
      [PublishStatus.INTERNAL]: 'Internal',
      [PublishStatus.PUBLIC]: 'Published'
    };
    return statusMap[status] || status;
  }

  /**
   * Phase 7: Format date range for display in filter chips
   * 
   * @param from - Start date (ISO string)
   * @param to - End date (ISO string)
   * @returns Formatted date range string
   */
  protected formatDateRange(from: string | null, to: string | null): string {
    if (!from && !to) return '';
    if (from && to) {
      return `${this.formatDisplayDate(from)} - ${this.formatDisplayDate(to)}`;
    }
    if (from) {
      return `After ${this.formatDisplayDate(from)}`;
    }
    return `Before ${this.formatDisplayDate(to!)}`;
  }

  /**
   * Phase 7: Format single date for display
   * 
   * @param dateString - ISO date string
   * @returns Formatted date string (e.g., "Jan 15, 2024")
   */
  private formatDisplayDate(dateString: string): string {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric' 
    });
  }

  /**
   * Phase 7: Get selected post type name for display
   * 
   * @returns Post type name or empty string
   */
  protected getSelectedPostTypeName(): string {
    return this.selectedPostTypeName();
  }

  /**
   * Update search term and trigger search
   */
  protected onSearchChange(value: string): void {
    this.searchTerm.set(value);
    const minLength = this.getMinSearchLength();

    if (value.trim().length >= minLength) {
      this.isLoading.set(true);
      this.errorMessage.set(null);
      this.searchSubject$.next(value);
    } else if (value.trim().length === 0) {
      this.clearSearch();
    }
  }

  /**
   * Handle status filter change
   * No need to re-search - client-side filtering handles it
   */
  protected onStatusFilterChange(status: PublishStatus | 'all'): void {
    this.selectedStatusFilter.set(status);
    // The filteredPosts computed will automatically update
  }
  
  /**
   * Perform immediate search without debouncing
   */
  private performSearchImmediate(searchTerm: string): void {
    this.performSearch(searchTerm).subscribe({
      next: (results) => {
        this.searchResults.set(results);
        this.hasSearched.set(true);
        this.isLoading.set(false);
        this.errorMessage.set(null);
      },
      error: (error) => {
        console.error('Search error:', error);
        this.errorMessage.set('Failed to search posts. Please try again.');
        this.isLoading.set(false);
      }
    });
  }

  /**
   * Trigger search from filter changes
   * This method allows filters to work independently without requiring initial text search
   */
  private triggerFilterSearch(): void {
    this.isLoading.set(true);
    this.errorMessage.set(null);
    
    // Use current search term (can be empty)
    const searchTerm = this.searchTerm();
    
    this.performSearch(searchTerm).subscribe({
      next: (results) => {
        this.searchResults.set(results);
        this.hasSearched.set(true);
        this.isLoading.set(false);
        this.errorMessage.set(null);
      },
      error: (error) => {
        console.error('Filter search error:', error);
        this.errorMessage.set('Failed to apply filters. Please try again.');
        this.isLoading.set(false);
      }
    });
  }

  /**
   * Get publish status badge class
   */
  protected getStatusBadgeClass(status: PublishStatus): string {
    const baseClasses = 'px-2 py-1 text-xs font-medium rounded-full transition-colors';
    
    switch (status) {
      case PublishStatus.DRAFT:
        return `${baseClasses} bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300`;
      case PublishStatus.INTERNAL:
        return `${baseClasses} bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300`;
      case PublishStatus.PUBLIC:
        return `${baseClasses} bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300`;
      default:
        return baseClasses;
    }
  }

  /**
   * Get publish status display text
   */
  protected getStatusDisplayText(status: PublishStatus): string {
    switch (status) {
      case PublishStatus.DRAFT:
        return 'Draft';
      case PublishStatus.INTERNAL:
        return 'Internal';
      case PublishStatus.PUBLIC:
        return 'Published';
      default:
        return status;
    }
  }

  /**
   * Handle row click
   */
  protected onRowClick(postId: string, event: MouseEvent): void {
    // Don't toggle if clicking on checkbox directly
    if ((event.target as HTMLElement).tagName === 'INPUT') {
      return;
    }
    
    if (this.canSelectPost(postId)) {
      this.togglePost(postId);
    }
  }

  /**
   * Handle keyboard navigation on row
   */
  protected onRowKeydown(postId: string, event: KeyboardEvent): void {
    if (event.key === ' ' || event.key === 'Enter') {
      event.preventDefault();
      if (this.canSelectPost(postId)) {
        this.togglePost(postId);
      }
    }
  }

  /**
   * Set hovered post
   */
  protected onRowMouseEnter(postId: string): void {
    this.hoveredPostId.set(postId);
  }

  /**
   * Clear hovered post
   */
  protected onRowMouseLeave(): void {
    this.hoveredPostId.set(null);
  }

  /**
   * Confirm selection
   */
  protected onConfirm(): void {
    const allPosts = this.searchResults();
    const selectedIds = Array.from(this.selectedPostIds());
    
    const selectedPosts: SelectedBlogPost[] = selectedIds
      .map(id => {
        const post = allPosts.find(p => p._id === id);
        return post ? { id: post._id, title: post.title } : null;
      })
      .filter((post): post is SelectedBlogPost => post !== null);

    // If used in dialog, close with result
    if (this.dialogRef) {
      this.dialogRef.close({
        selectedPosts,
        cancelled: false
      });
    } else {
      // Otherwise emit output event
      this.selectionConfirmed.emit(selectedPosts);
    }
  }

  /**
   * Cancel selection
   */
  protected onCancel(): void {
    // If used in dialog, close with cancelled result
    if (this.dialogRef) {
      this.dialogRef.close({
        selectedPosts: [],
        cancelled: true
      });
    } else {
      // Otherwise emit output event
      this.selectionCancelled.emit();
    }
  }

  /**
   * Handle escape key to close dialog
   */
  protected onEscapeKey(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      this.onCancel();
    }
  }
}
